// All 32 current NFL team codes used by nflverse (our schedule/pbp/stats source, see
// services/data_ingestion/nflverse_provider.py) -- confirmed against the live 2026 season's
// games.csv home_team/away_team values, not guessed. Every logo_url below was fetched and
// verified (HTTP 200 + image/png content-type) directly from ESPN's team-logo CDN
// (https://a.espncdn.com/i/teamlogos/nfl/500/{slug}.png), the same CDN pattern widely used for
// NFL team marks. Three nflverse codes don't match ESPN's own slug and are mapped explicitly
// below: nflverse "LA" (Rams) -> espn "lar", nflverse "WAS" (Commanders) -> espn "wsh" -- every
// other code matches ESPN's slug lowercased as-is. `TeamLogo` (components/shared/TeamLogo.jsx)
// already falls back to a plain abbreviation badge if a URL ever fails to load -- no logo here
// is fabricated or unverified.
//
// `color` is each team's own official primary brand color (public-domain team-identity facts,
// the same hex values used on nflverse's own team-color reference table and every major sports
// site) -- used only as a thin ring around the logo circle (see TeamLogo's `ringColor`) so 32
// teams read as visually distinct instead of sitting on the same plain white circle. Never used
// as a solid fill behind the mark, since several logos include white ink that would lose
// contrast against their own team's color.
const ESPN_LOGO = (slug) => `https://a.espncdn.com/i/teamlogos/nfl/500/${slug}.png`;

export const NFL_TEAMS = {
  ARI: { name: "Arizona Cardinals", logo_url: ESPN_LOGO("ari"), color: "#97233F" },
  ATL: { name: "Atlanta Falcons", logo_url: ESPN_LOGO("atl"), color: "#A71930" },
  BAL: { name: "Baltimore Ravens", logo_url: ESPN_LOGO("bal"), color: "#241773" },
  BUF: { name: "Buffalo Bills", logo_url: ESPN_LOGO("buf"), color: "#00338D" },
  CAR: { name: "Carolina Panthers", logo_url: ESPN_LOGO("car"), color: "#0085CA" },
  CHI: { name: "Chicago Bears", logo_url: ESPN_LOGO("chi"), color: "#C83803" },
  CIN: { name: "Cincinnati Bengals", logo_url: ESPN_LOGO("cin"), color: "#FB4F14" },
  CLE: { name: "Cleveland Browns", logo_url: ESPN_LOGO("cle"), color: "#FF3C00" },
  DAL: { name: "Dallas Cowboys", logo_url: ESPN_LOGO("dal"), color: "#003594" },
  DEN: { name: "Denver Broncos", logo_url: ESPN_LOGO("den"), color: "#FB4F14" },
  DET: { name: "Detroit Lions", logo_url: ESPN_LOGO("det"), color: "#0076B6" },
  GB: { name: "Green Bay Packers", logo_url: ESPN_LOGO("gb"), color: "#203731" },
  HOU: { name: "Houston Texans", logo_url: ESPN_LOGO("hou"), color: "#A71930" },
  IND: { name: "Indianapolis Colts", logo_url: ESPN_LOGO("ind"), color: "#002C5F" },
  JAX: { name: "Jacksonville Jaguars", logo_url: ESPN_LOGO("jax"), color: "#006778" },
  KC: { name: "Kansas City Chiefs", logo_url: ESPN_LOGO("kc"), color: "#E31837" },
  LA: { name: "Los Angeles Rams", logo_url: ESPN_LOGO("lar"), color: "#FFA300" },
  LAC: { name: "Los Angeles Chargers", logo_url: ESPN_LOGO("lac"), color: "#0080C6" },
  LV: { name: "Las Vegas Raiders", logo_url: ESPN_LOGO("lv"), color: "#A5ACAF" },
  MIA: { name: "Miami Dolphins", logo_url: ESPN_LOGO("mia"), color: "#008E97" },
  MIN: { name: "Minnesota Vikings", logo_url: ESPN_LOGO("min"), color: "#4F2683" },
  NE: { name: "New England Patriots", logo_url: ESPN_LOGO("ne"), color: "#C60C30" },
  NO: { name: "New Orleans Saints", logo_url: ESPN_LOGO("no"), color: "#D3BC8D" },
  NYG: { name: "New York Giants", logo_url: ESPN_LOGO("nyg"), color: "#0B2265" },
  NYJ: { name: "New York Jets", logo_url: ESPN_LOGO("nyj"), color: "#125740" },
  PHI: { name: "Philadelphia Eagles", logo_url: ESPN_LOGO("phi"), color: "#004C54" },
  PIT: { name: "Pittsburgh Steelers", logo_url: ESPN_LOGO("pit"), color: "#FFB612" },
  SEA: { name: "Seattle Seahawks", logo_url: ESPN_LOGO("sea"), color: "#69BE28" },
  SF: { name: "San Francisco 49ers", logo_url: ESPN_LOGO("sf"), color: "#AA0000" },
  TB: { name: "Tampa Bay Buccaneers", logo_url: ESPN_LOGO("tb"), color: "#D50A0A" },
  TEN: { name: "Tennessee Titans", logo_url: ESPN_LOGO("ten"), color: "#4B92DB" },
  WAS: { name: "Washington Commanders", logo_url: ESPN_LOGO("wsh"), color: "#5A1414" },
};

// `TeamLogo` expects `{ name, abbreviation, logo_url }` (see components/shared/TeamLogo.jsx) --
// the NFL preview response only sends the bare abbreviation string (e.g. "KC"), so this wraps
// it into that same shape, plus `color` for TeamLogo's optional `ringColor` prop. An
// unrecognized code (should never happen -- the registry above is exhaustive for nflverse's
// current 32 codes) still renders via TeamLogo's own abbreviation fallback rather than crashing.
export const nflTeamObject = (code) => ({
  abbreviation: code,
  name: NFL_TEAMS[code]?.name || code,
  logo_url: NFL_TEAMS[code]?.logo_url || null,
  color: NFL_TEAMS[code]?.color || null,
});

